import {JSONSchema7, JSONSchema7Array} from 'json-schema';
import {get, isEqual, unset} from 'lodash';

/**
 * A stateless helper for resolving  working with JSON‑Schema (draft‑7).
 * @see https://json-schema.org/understanding-json-schema/reference/refs.html
 */
export abstract class JsonSchemaUtils {
    /**
     * Resolve an internal JSON‑Schema `$ref` against a root schema.
     *
     * @param ref A reference string of the form
     *            `#/path/to/definition` or
     *            `baseId#/path/to/definition`.  The method only accepts
     *            references that point back to the current root schema
     *            (`$id` match).  If the base ID does not match the
     *            `$id` of the supplied `rootSchema`, `undefined` is
     *            returned.
     * @param rootSchema The root JSON‑Schema against which the reference
     *                   will be resolved.
     * @returns The resolved `JSONSchema7` fragment, or `undefined` if any
     *          part of the path cannot be found.
     * @throws {@link Error} if the reference string does not contain a
     *          `#` and is therefore considered unsupported.
     */
    public static resolveRef(ref: string, rootSchema: JSONSchema7): JSONSchema7 | undefined {
        // Handle only fragments that reference the same root schema.
        if (ref.indexOf('#') !== -1) {
            const poundParts = ref.split('#').filter((x) => x.trim().length != 0);

            if (poundParts.length == 2) {
                const refBaseSchemaId = poundParts[0];

                // The base part (before the `#`) must match the root schema's $id.
                if (rootSchema.$id !== refBaseSchemaId) {
                    return undefined;
                }
            }

            // Example:
            // DatatypeInput.schema.json#/definitions/DatatypeInputLocation.schema.json
            const pathParts = poundParts[poundParts.length == 2 ? 1 : 0].split('/');
            let currentLocation: JSONSchema7 = rootSchema;

            // Walk each segment of the path, ignoring empty segments that
            // arise from leading or consecutive slashes.
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < pathParts.length; ++i) {
                const part = pathParts[i].trim();
                if (part === '') continue;

                // If the part does not exist on the current schema node,
                // the reference cannot be resolved.
                if (!(part in currentLocation)) {
                    return undefined;
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                currentLocation = (currentLocation as any)[part] as JSONSchema7;
            }

            return currentLocation;
        }

        // Currently only internal references are supported.
        throw new Error(`Unsupported Json schema ref '${ref}'`);
    }

    public static parseRowReference(
        ref: string,
        rootSchema: JSONSchema7
    ): {
        isRowReference: boolean;
        resolvedRef: JSONSchema7 | undefined;
        propertyName: string | undefined;
        referenceExpression: string | undefined;
    } {
        const poundParts = ref.split('#').filter((x) => x.trim().length != 0);
        if (poundParts.length != 2 || poundParts[1] !== '/identifier')
            return {
                isRowReference: false,
                resolvedRef: undefined,
                propertyName: undefined,
                referenceExpression: undefined
            };

        // Reconstruct the left part only, we don't want to include the #/reference part
        const resolvedRef = JsonSchemaUtils.resolveRef('#' + poundParts[0], rootSchema);
        if (!resolvedRef)
            return {
                isRowReference: false,
                resolvedRef: undefined,
                propertyName: undefined,
                referenceExpression: undefined
            };

        if (!('identifier' in resolvedRef)) {
            throw new Error(`Resolved reference does not contain 'identifier' property`);
        }
        let referenceExpression = (('{{ ' + resolvedRef['identifier']) as string) + ' }}';
        if ('reference' in resolvedRef) {
            referenceExpression = resolvedRef['reference'] as string;
        }

        return {
            isRowReference: true,
            resolvedRef: resolvedRef,
            propertyName: resolvedRef['identifier'] as string,
            referenceExpression: referenceExpression
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * Recursively collect the property paths (as lodash-compatible path segment arrays) of every
     * `x-is-secret: true` leaf found within a schema, resolving `$ref`s against the supplied root schema.
     *
     * @param schema The (sub-)schema to inspect.
     * @param rootSchema The root schema used to resolve any `$ref` encountered.
     * @param prefix Path segments accumulated so far (used internally for recursion).
     * @returns An array of property paths, each path being an array of string segments.
     */
    public static collectSecretLeafPaths(
        schema: JSONSchema7,
        rootSchema: JSONSchema7,
        prefix: string[] = []
    ): string[][] {
        let resolved = schema;
        if (resolved.$ref) {
            const target = JsonSchemaUtils.resolveRef(resolved.$ref, rootSchema);
            if (!target) return [];

            resolved = target;
        }

        if ((resolved as Record<string, unknown>)['x-is-secret'] === true) {
            return [prefix];
        }

        if (!resolved.properties) return [];

        const paths: string[][] = [];
        for (const key of Object.keys(resolved.properties)) {
            const subSchema = resolved.properties[key];
            if (subSchema === true || subSchema === false) continue;

            paths.push(...JsonSchemaUtils.collectSecretLeafPaths(subSchema, rootSchema, [...prefix, key]));
        }

        return paths;
    }

    /**
     * Given a set of secret leaf paths, remove from `current` every path whose value is unchanged
     * compared to `initial`. Paths absent from `initial` are left untouched in `current` (there is
     * nothing to compare against, so the value is considered intentionally provided/changed).
     *
     * @param current The current (possibly edited) value.
     * @param initial The originally loaded value, used as the comparison baseline.
     * @param paths Secret leaf paths, as produced by {@link collectSecretLeafPaths}.
     * @returns `current`, mutated in place, with unchanged secret leaves removed.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static stripUnchangedSecretsByPaths(current: any, initial: any, paths: string[][]): any {
        for (const path of paths) {
            if (path.length === 0) continue;

            const currentValue = get(current, path);
            const initialValue = get(initial, path);

            if (initialValue === undefined) continue;

            if (isEqual(currentValue, initialValue)) {
                unset(current, path);
            }
        }

        return current;
    }

    public static autoDatePropety(optional = false): JSONSchema7 {
        return {
            type: optional ? ['string', 'null'] : 'string',
            format: 'date'
        };
    }
    public static autoEnumPropety(enumValues: JSONSchema7Array, optional = false): JSONSchema7 {
        return {
            type: optional ? ['string', 'null'] : 'string',
            enum: enumValues
        };
    }
}
